import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { ImageUpload } from "../components/ImageUpload";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Eye, EyeOff } from "lucide-react";

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", city: "", email: "", phone: "", password: "", website: "", logo_path: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/register", form);
      setDone(true);
      toast.success("Registracija poslata");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="App min-h-screen bg-background">
        <Navbar />
        <div className="max-w-md mx-auto px-5 py-20 text-center">
          <CheckCircle2 className="w-16 h-16 mx-auto text-[hsl(var(--secondary))] mb-6" />
          <h1 className="font-display text-3xl font-black tracking-tighter mb-3">Registracija poslata!</h1>
          <p className="text-muted-foreground mb-8">
            Vaš klub čeka odobrenje mastera UPUS CG. Nakon odobrenja moći ćete se prijaviti i dodavati članove.
          </p>
          <Button className="rounded-none bg-primary hover:bg-primary/90" onClick={() => navigate("/prijava")} data-testid="register-goto-login">
            Idi na prijavu
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-background">
      <Navbar />
      <div className="max-w-lg mx-auto px-5 py-14">
        <div className="border-2 border-foreground bg-card p-8">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Nova članica</div>
          <h1 className="font-display text-3xl font-black tracking-tighter mb-6">Registruj klub</h1>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Logo kluba</Label>
              <div className="mt-1.5">
                <ImageUpload value={form.logo_path} onChange={(p) => setForm({ ...form, logo_path: p })} label="Logo" testId="register-logo-upload" />
              </div>
            </div>
            <div>
              <Label htmlFor="name">Ime kluba *</Label>
              <Input id="name" className="rounded-none border-2 mt-1.5" value={form.name} onChange={set("name")} required data-testid="register-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">Grad *</Label>
                <Input id="city" className="rounded-none border-2 mt-1.5" value={form.city} onChange={set("city")} required data-testid="register-city" />
              </div>
              <div>
                <Label htmlFor="phone">Telefon *</Label>
                <Input id="phone" className="rounded-none border-2 mt-1.5" value={form.phone} onChange={set("phone")} required data-testid="register-phone" />
              </div>
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" className="rounded-none border-2 mt-1.5" value={form.email} onChange={set("email")} required data-testid="register-email" />
            </div>
            <div>
              <Label htmlFor="website">Web stranica (opciono)</Label>
              <Input id="website" className="rounded-none border-2 mt-1.5" value={form.website} onChange={set("website")} placeholder="www.klub.me" data-testid="register-website" />
            </div>
            <div>
              <Label htmlFor="password">Lozinka *</Label>
              <Input id="password" type="password" className="rounded-none border-2 mt-1.5" value={form.password} onChange={set("password")} required minLength={6} data-testid="register-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-none bg-primary hover:bg-primary/90 h-11" data-testid="register-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Pošalji registraciju"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            Već imate nalog?{" "}
            <Link to="/prijava" className="text-primary font-semibold hover:underline">Prijava</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
